import * as React from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';
import {
  TextField,
  Autocomplete,
  Typography,
  Box,
  Paper,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import type { PaperProps } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useGoogleMapsLoaded } from '../../utils/googleMaps';
import { speedysseyInputSx } from './MuiInputs';

export interface PlaceDetails {
  formattedAddress: string;
  lat?: number;
  lng?: number;
  postalCode?: string;
  province?: string;
  city?: string;
  placeId?: string;
}

interface CanadaAddressAutocompleteProps {
  id: string;
  value: string;
  onChange: (address: string, details?: PlaceDetails) => void;
  placeholder?: string;
  error?: string;
  required?: boolean;
  rows?: number;
  label?: string;
  helperText?: string;
}

interface PlaceType {
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text?: string;
  };
  place_id: string;
}

// Custom Paper with Google attribution branding matching business-web-ui
function CustomPaper(props: PaperProps) {
  return (
    <Paper
      {...props}
      sx={{
        borderRadius: '16px',
        mt: 1,
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
        border: '1px solid #E5E7EB',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: '#F9FAFB',
          borderBottom: '1px solid #F3F4F6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '11px',
          fontWeight: 600,
          color: '#6B7280',
        }}
      >
        <span>🇨🇦 Canadian Addresses (Google Places)</span>
        <span style={{ fontSize: '10px', color: '#9CA3AF' }}>Select address</span>
      </Box>

      {props.children}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1,
          bgcolor: '#F9FAFB',
          borderTop: '1px solid #F3F4F6',
          fontSize: '10px',
          color: '#9CA3AF',
        }}
      >
        <span>Powered by Google</span>
        <svg
          aria-label="Google"
          height="14"
          preserveAspectRatio="xMidYMid meet"
          viewBox="0 0 98 18"
          width="70"
        >
          <path
            fill="#4285F4"
            d="M7.08 13.96a6.9 6.9 0 01-4.99-2.05A6.7 6.7 0 010 6.98Q0 4.1 2.09 2.05A6.9 6.9 0 017.08 0a6.7 6.7 0 014.79 1.92l-1.35 1.35a4.8 4.8 0 00-3.44-1.36q-2.1 0-3.55 1.48a5 5 0 00-1.45 3.59q0 2.12 1.46 3.59a4.8 4.8 0 003.55 1.48 4.8 4.8 0 003.53-1.4q.84-.84 1.04-2.4H7.08v-1.9h6.42a6 6 0 01.1 1.19q0 2.8-1.65 4.46a6.4 6.4 0 01-4.87 1.96"
          />
        </svg>
      </Box>
    </Paper>
  );
}

export const CanadaAddressAutocomplete: React.FC<CanadaAddressAutocompleteProps> = ({
  id,
  value,
  onChange,
  placeholder = 'Start typing address in Canada (e.g. 100 King St W, Toronto, ON)...',
  error,
  required = false,
  rows = 3,
  label = 'Address',
  helperText,
}) => {
  const { isLoaded } = useGoogleMapsLoaded();
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState<readonly PlaceType[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<PlaceType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCanadaVerified, setIsCanadaVerified] = useState(false);

  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const debounceTimerRef = useRef<number | null>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const getAutocompleteService = (): google.maps.places.AutocompleteService | null => {
    if (autocompleteServiceRef.current) return autocompleteServiceRef.current;
    if (typeof window !== 'undefined' && window.google?.maps?.places) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      return autocompleteServiceRef.current;
    }
    return null;
  };

  const getPlacesService = (): google.maps.places.PlacesService | null => {
    if (placesServiceRef.current) return placesServiceRef.current;
    if (typeof window !== 'undefined' && window.google?.maps?.places) {
      const dummyDiv = document.createElement('div');
      placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv);
      return placesServiceRef.current;
    }
    return null;
  };

  const fetchPredictions = (query: string) => {
    const service = getAutocompleteService();

    if (!query || query.trim().length < 2) {
      setOptions([]);
      setIsLoading(false);
      return;
    }

    if (!service) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Restricted to Canada via componentRestrictions
    service.getPlacePredictions(
      {
        input: query,
        componentRestrictions: { country: 'ca' },
        types: ['geocode'],
      },
      (results, status) => {
        if (
          status === window.google?.maps?.places?.PlacesServiceStatus?.OK &&
          results &&
          results.length > 0
        ) {
          setIsLoading(false);
          const parsed: PlaceType[] = results.map((p) => ({
            description: p.description,
            structured_formatting: {
              main_text: p.structured_formatting?.main_text || p.description,
              secondary_text: p.structured_formatting?.secondary_text || '',
            },
            place_id: p.place_id,
          }));
          setOptions(parsed);
        } else {
          // Fallback to general Canadian locations/establishments
          service.getPlacePredictions(
            {
              input: query,
              componentRestrictions: { country: 'ca' },
            },
            (fallbackResults, fallbackStatus) => {
              setIsLoading(false);
              if (
                fallbackStatus === window.google?.maps?.places?.PlacesServiceStatus?.OK &&
                fallbackResults &&
                fallbackResults.length > 0
              ) {
                const parsed: PlaceType[] = fallbackResults.map((p) => ({
                  description: p.description,
                  structured_formatting: {
                    main_text: p.structured_formatting?.main_text || p.description,
                    secondary_text: p.structured_formatting?.secondary_text || '',
                  },
                  place_id: p.place_id,
                }));
                setOptions(parsed);
              } else {
                setOptions([]);
              }
            }
          );
        }
      }
    );
  };

  const handlePlaceSelect = (place: PlaceType) => {
    setSelectedPlace(place);
    setInputValue(place.description);
    setIsCanadaVerified(true);

    const placesService = getPlacesService();

    if (placesService && place.place_id) {
      placesService.getDetails(
        {
          placeId: place.place_id,
          fields: ['formatted_address', 'geometry', 'address_components', 'name'],
        },
        (details, status) => {
          if (
            status === window.google?.maps?.places?.PlacesServiceStatus?.OK &&
            details &&
            details.formatted_address
          ) {
            const lat = details.geometry?.location?.lat();
            const lng = details.geometry?.location?.lng();

            let postalCode: string | undefined;
            let province: string | undefined;
            let city: string | undefined;

            if (details.address_components) {
              for (const comp of details.address_components) {
                if (comp.types.includes('postal_code')) {
                  postalCode = comp.long_name;
                }
                if (comp.types.includes('administrative_area_level_1')) {
                  province = comp.short_name || comp.long_name;
                }
                if (
                  comp.types.includes('locality') ||
                  comp.types.includes('sublocality')
                ) {
                  city = comp.long_name;
                }
              }
            }

            const placeData: PlaceDetails = {
              formattedAddress: details.formatted_address,
              lat,
              lng,
              postalCode,
              province,
              city,
              placeId: place.place_id,
            };

            setInputValue(details.formatted_address);
            onChange(details.formatted_address, placeData);
          } else {
            onChange(place.description, { formattedAddress: place.description });
          }
        }
      );
    } else {
      onChange(place.description, { formattedAddress: place.description });
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        freeSolo
        fullWidth
        id={id}
        disableClearable={false}
        filterOptions={(x) => x}
        options={options}
        value={selectedPlace || inputValue}
        inputValue={inputValue}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.description
        }
        onInputChange={(e, newInput, reason) => {
          setInputValue(newInput);
          onChange(newInput);
          setIsCanadaVerified(false);

          if (reason === 'input') {
            if (debounceTimerRef.current) {
              window.clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = window.setTimeout(() => {
              fetchPredictions(newInput);
            }, 300);
          }
        }}
        onChange={(e, newVal) => {
          if (newVal && typeof newVal !== 'string') {
            handlePlaceSelect(newVal);
          } else if (typeof newVal === 'string') {
            onChange(newVal);
          }
        }}
        slots={{
          paper: CustomPaper,
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            required={required}
            error={!!error}
            helperText={error || helperText}
            multiline
            rows={rows}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                    <LocationOnIcon sx={{ color: isCanadaVerified ? '#10B981' : '#9CA3AF' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    {isLoading && <CircularProgress size={16} sx={{ color: '#F55109' }} />}
                    {isCanadaVerified && (
                      <CheckCircleIcon sx={{ color: '#10B981', fontSize: 20 }} />
                    )}
                    {params.InputProps.endAdornment}
                  </Box>
                ),
              },
            }}
            sx={{
              ...speedysseyInputSx,
              ...(isCanadaVerified && {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#10B981 !important',
                },
              }),
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          const opt: PlaceType =
            typeof option === 'string'
              ? {
                  description: option,
                  structured_formatting: { main_text: option, secondary_text: '' },
                  place_id: '',
                }
              : option;
          return (
            <li key={key} {...otherProps}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 0.5,
                  width: '100%',
                }}
              >
                <LocationOnIcon sx={{ color: '#F55109', fontSize: 20, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1F2937' }} noWrap>
                    {opt.structured_formatting.main_text}
                  </Typography>
                  {opt.structured_formatting.secondary_text && (
                    <Typography variant="caption" sx={{ color: '#6B7280' }} noWrap>
                      {opt.structured_formatting.secondary_text}
                    </Typography>
                  )}
                </Box>
              </Box>
            </li>
          );
        }}
      />
    </Box>
  );
};
